import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Award, RefreshCw, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';

// Test Type Badge Component
const TestTypeBadge = ({ testType }) => {
  const typeConfig = {
    free: {
      label: '10Q',
      color: 'bg-blue-100 text-blue-700 border-blue-300',
      description: 'Quick Test'
    },
    comprehensive: {
      label: '25Q',
      color: 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-purple-700 shadow-md',
      description: 'Comprehensive Test - More Accurate',
      icon: <CheckCircle2 className="w-3 h-3" />
    }
  }

  const badge = typeConfig[testType] || typeConfig.free

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full border ${badge.color}`}
      title={badge.description}
    >
      {badge.icon}
      {badge.label}
    </span>
  )
}

const LeaderboardPage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const { getLeaderboard } = useApi();

  const loadLeaderboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getLeaderboard('global', { timeFilter: 'all-time' });
      setLeaderboard(response.users || []);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeaderboard();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Leaderboard</h3>
            <p className="text-red-700 mb-4">{error}</p>
            <button
              onClick={loadLeaderboard}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              <RefreshCw className="mr-2 w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Trophy className="w-12 h-12 text-yellow-500 mr-3 relative -top-3" />
            <h1 className="text-4xl font-bold text-gray-900">Leaderboard</h1>
          </div>
          <p className="text-xl text-gray-600">
            See how you rank against other test takers worldwide
          </p>
        </div>

        {leaderboard.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Rankings Yet</h3>
            <p className="text-gray-600 mb-6">
              Be the first to appear on the leaderboard!
            </p>
            <a
              href="/"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Take a Test
              <Trophy className="ml-2 w-4 h-4" />
            </a>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Top Performers
              </h2>
              <button
                onClick={loadLeaderboard}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </button>
            </div>

            <div className="divide-y divide-gray-200">
              {(showAll ? leaderboard : leaderboard.slice(0, 10)).map((entry, index) => {
                const rank = index + 1;
                return (
                  <div
                    key={entry.id}
                    className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center justify-center w-10 h-10">
                        {rank === 1 && <Trophy className="w-6 h-6 text-yellow-500" />}
                        {rank === 2 && <Medal className="w-6 h-6 text-gray-400" />}
                        {rank === 3 && <Award className="w-6 h-6 text-orange-500" />}
                        {rank > 3 && <span className="text-lg font-bold text-gray-600">#{rank}</span>}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {entry.name || 'Anonymous'}
                          </h3>
                          {entry.testType && <TestTypeBadge testType={entry.testType} />}
                        </div>
                        {entry.tests && (
                          <p className="text-sm text-gray-500">
                            {entry.tests} test{entry.tests !== 1 ? 's' : ''} completed
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        {entry.score}
                      </div>
                      <div className="text-sm text-gray-500">
                        {entry.percentile}th percentile
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {leaderboard.length > 10 && !showAll && (
              <div className="px-6 py-4 bg-gray-50 border-t text-center">
                <button
                  onClick={() => setShowAll(true)}
                  className="text-sm text-gray-600 hover:text-gray-900 underline"
                >
                  View all {leaderboard.length} rankings
                </button>
              </div>
            )}

            {showAll && leaderboard.length > 10 && (
              <div className="px-6 py-4 bg-gray-50 border-t text-center">
                <button
                  onClick={() => setShowAll(false)}
                  className="text-sm text-gray-600 hover:text-gray-900 underline"
                >
                  Show top 10 only
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            Want to Join the Leaderboard?
          </h3>
          <p className="text-blue-700 mb-4">
            Take a test and log in to see your ranking among other users!
          </p>
          <a
            href="/"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Take a Test
            <Trophy className="ml-2 w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;